/**
 * Solids: the connected pieces a mesh's triangles make, and whether each has an
 * inside. Plain numbers, no Babylon. Matrices are Babylon's row-vector layout:
 * `p' = [x, y, z, 1] * m`, so rows 0 to 2 are the local axes and row 3 the origin.
 */

export type Vec3 = [number, number, number];

/** One connected piece of a mesh, in mesh space. A baked part is usually several: a body and its studs. */
export interface FitShape {
    /** Welded vertex positions, xyz per vertex. */
    readonly positions: Float64Array;
    /** Three vertex indices per triangle; wound outward when `closed`. */
    readonly triangles: Uint32Array;
    /** Every edge is run once each way by the triangles that share it, so the shape has an inside. */
    readonly closed: boolean;
    /** Closed, and every vertex is on or behind every face: inside is the intersection of the faces' half-spaces. */
    readonly convex: boolean;
}

/** A shape placed in a frame. */
export interface FitSolid {
    readonly shape: FitShape;
    /** Placed vertex positions, xyz per vertex. */
    readonly positions: Float64Array;
    readonly lo: Vec3;
    readonly hi: Vec3;
    /** Per triangle: unit normal (outward when closed), plane offset `n·p`, area. Five numbers each. */
    readonly faces: Float64Array;
}

const FACE = 5;

function extentOf(p: ArrayLike<number>, count: number): number {
    let size = 0;
    for (let axis = 0; axis < 3; axis++) {
        let lo = Infinity, hi = -Infinity;
        for (let i = 0; i < count; i++) {
            const v = p[i * 3 + axis];
            if (v < lo) lo = v;
            if (v > hi) hi = v;
        }
        if (count && hi - lo > size) size = hi - lo;
    }
    return size;
}

/**
 * The connected pieces of a triangle mesh (vertices welded by position, triangles joined across shared edges).
 * A closed piece whose triangles disagree on direction is turned to agree, and outward.
 */
export function fitShapes(positions: ArrayLike<number>, indices: ArrayLike<number>): FitShape[] {
    const count = Math.floor(positions.length / 3);
    const tol = Math.max(extentOf(positions, count) * 1e-6, 1e-12);
    const weld = new Map<string, number>();
    const remap = new Uint32Array(count);
    const welded: number[] = [];
    for (let i = 0; i < count; i++) {
        const x = positions[i * 3], y = positions[i * 3 + 1], z = positions[i * 3 + 2];
        const key = `${Math.round(x / tol)},${Math.round(y / tol)},${Math.round(z / tol)}`;
        let id = weld.get(key);
        if (id === undefined) {
            id = welded.length / 3;
            weld.set(key, id);
            welded.push(x, y, z);
        }
        remap[i] = id;
    }
    const tris: number[] = [];
    for (let t = 0; t + 2 < indices.length; t += 3) {
        const a = remap[indices[t]], b = remap[indices[t + 1]], c = remap[indices[t + 2]];
        if (a !== b && b !== c && a !== c) tris.push(a, b, c);
    }
    const triCount = tris.length / 3;
    const vertexCount = welded.length / 3;
    const edgeKey = (a: number, b: number): number => (a < b ? a * vertexCount + b : b * vertexCount + a);
    // Join triangles that share an edge.
    const parent = new Int32Array(triCount).map((_, i) => i);
    const find = (i: number): number => {
        while (parent[i] !== i) { parent[i] = parent[parent[i]]; i = parent[i]; }
        return i;
    };
    const firstOnEdge = new Map<number, number>();
    for (let t = 0; t < triCount; t++) {
        for (let j = 0; j < 3; j++) {
            const key = edgeKey(tris[t * 3 + j], tris[t * 3 + (j + 1) % 3]);
            const other = firstOnEdge.get(key);
            if (other === undefined) firstOnEdge.set(key, t);
            else parent[find(t)] = find(other);
        }
    }
    const groups = new Map<number, number[]>();
    for (let t = 0; t < triCount; t++) {
        const root = find(t);
        let list = groups.get(root);
        if (!list) groups.set(root, list = []);
        list.push(t);
    }
    const shapes: FitShape[] = [];
    for (const list of groups.values()) {
        const local = new Map<number, number>();
        const pos: number[] = [];
        const tri = new Uint32Array(list.length * 3);
        list.forEach((t, k) => {
            for (let j = 0; j < 3; j++) {
                const v = tris[t * 3 + j];
                let id = local.get(v);
                if (id === undefined) {
                    id = pos.length / 3;
                    local.set(v, id);
                    pos.push(welded[v * 3], welded[v * 3 + 1], welded[v * 3 + 2]);
                }
                tri[k * 3 + j] = id;
            }
        });
        const p = Float64Array.from(pos);
        const closed = orient(tri, pos.length / 3);
        if (closed && signedVolume(p, tri) < 0) {
            for (let t = 0; t < tri.length; t += 3) { const s = tri[t + 1]; tri[t + 1] = tri[t + 2]; tri[t + 2] = s; }
        }
        shapes.push({ positions: p, triangles: tri, closed, convex: closed && isConvex(p, tri) });
    }
    return shapes;
}

/**
 * Make the triangles of one piece agree on direction, in place, and say whether the piece is closed.
 * With every edge on exactly two triangles, neighbours are turned to run their shared edge in opposite
 * directions; otherwise (an edge on four triangles, say) the piece is closed only if it already balances.
 */
function orient(tri: Uint32Array, vertexCount: number): boolean {
    const n = tri.length / 3;
    const key = (a: number, b: number): number => (a < b ? a * vertexCount + b : b * vertexCount + a);
    const onEdge = new Map<number, number[]>();
    for (let t = 0; t < n; t++) {
        for (let j = 0; j < 3; j++) {
            const k = key(tri[t * 3 + j], tri[t * 3 + (j + 1) % 3]);
            const list = onEdge.get(k);
            if (list) list.push(t); else onEdge.set(k, [t]);
        }
    }
    const balances = (): boolean => {
        const balance = new Map<number, number>();
        for (let t = 0; t < n; t++) {
            for (let j = 0; j < 3; j++) {
                const a = tri[t * 3 + j], b = tri[t * 3 + (j + 1) % 3];
                const k = key(a, b);
                balance.set(k, (balance.get(k) ?? 0) + (a < b ? 1 : -1));
            }
        }
        for (const v of balance.values()) if (v !== 0) return false;
        return true;
    };
    for (const list of onEdge.values()) if (list.length !== 2) return balances();
    const runs = (t: number, a: number, b: number): boolean => {
        for (let j = 0; j < 3; j++) if (tri[t * 3 + j] === a && tri[t * 3 + (j + 1) % 3] === b) return true;
        return false;
    };
    const seen = new Uint8Array(n);
    for (let start = 0; start < n; start++) {
        if (seen[start]) continue;
        seen[start] = 1;
        const stack = [start];
        while (stack.length) {
            const t = stack.pop()!;
            for (let j = 0; j < 3; j++) {
                const a = tri[t * 3 + j], b = tri[t * 3 + (j + 1) % 3];
                const pair = onEdge.get(key(a, b))!;
                const u = pair[0] === t ? pair[1] : pair[0];
                if (seen[u]) continue;
                seen[u] = 1;
                if (runs(u, a, b)) { const s = tri[u * 3 + 1]; tri[u * 3 + 1] = tri[u * 3 + 2]; tri[u * 3 + 2] = s; }
                stack.push(u);
            }
        }
    }
    return balances();
}

function signedVolume(p: Float64Array, tri: Uint32Array): number {
    let v = 0;
    for (let t = 0; t < tri.length; t += 3) {
        const a = tri[t] * 3, b = tri[t + 1] * 3, c = tri[t + 2] * 3;
        v += p[a] * (p[b + 1] * p[c + 2] - p[b + 2] * p[c + 1])
            - p[a + 1] * (p[b] * p[c + 2] - p[b + 2] * p[c])
            + p[a + 2] * (p[b] * p[c + 1] - p[b + 1] * p[c]);
    }
    return v / 6;
}

function isConvex(p: Float64Array, tri: Uint32Array): boolean {
    const eps = extentOf(p, p.length / 3) * 1e-6;
    for (let t = 0; t < tri.length; t += 3) {
        const a = tri[t] * 3, b = tri[t + 1] * 3, c = tri[t + 2] * 3;
        const ux = p[b] - p[a], uy = p[b + 1] - p[a + 1], uz = p[b + 2] - p[a + 2];
        const vx = p[c] - p[a], vy = p[c + 1] - p[a + 1], vz = p[c + 2] - p[a + 2];
        let nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx;
        const len = Math.hypot(nx, ny, nz);
        if (len === 0) continue;
        nx /= len; ny /= len; nz /= len;
        const d = nx * p[a] + ny * p[a + 1] + nz * p[a + 2];
        for (let i = 0; i < p.length; i += 3) if (nx * p[i] + ny * p[i + 1] + nz * p[i + 2] - d > eps) return false;
    }
    return true;
}

/** A shape placed by `matrix` (16 numbers, row-vector layout). A mirroring matrix keeps the faces outward. */
export function placeShape(shape: FitShape, matrix: ArrayLike<number>): FitSolid {
    const src = shape.positions, m = matrix;
    const p = new Float64Array(src.length);
    const lo: Vec3 = [Infinity, Infinity, Infinity], hi: Vec3 = [-Infinity, -Infinity, -Infinity];
    for (let i = 0; i < src.length; i += 3) {
        const x = src[i], y = src[i + 1], z = src[i + 2];
        p[i] = x * m[0] + y * m[4] + z * m[8] + m[12];
        p[i + 1] = x * m[1] + y * m[5] + z * m[9] + m[13];
        p[i + 2] = x * m[2] + y * m[6] + z * m[10] + m[14];
        for (let k = 0; k < 3; k++) { if (p[i + k] < lo[k]) lo[k] = p[i + k]; if (p[i + k] > hi[k]) hi[k] = p[i + k]; }
    }
    const det = m[0] * (m[5] * m[10] - m[6] * m[9]) - m[1] * (m[4] * m[10] - m[6] * m[8]) + m[2] * (m[4] * m[9] - m[5] * m[8]);
    return { shape, positions: p, lo, hi, faces: facesOf(p, shape.triangles, det < 0 ? -1 : 1) };
}

function facesOf(p: Float64Array, tri: Uint32Array, sign: number): Float64Array {
    const f = new Float64Array(tri.length / 3 * FACE);
    for (let t = 0, o = 0; t < tri.length; t += 3, o += FACE) {
        const a = tri[t] * 3, b = tri[t + 1] * 3, c = tri[t + 2] * 3;
        const ux = p[b] - p[a], uy = p[b + 1] - p[a + 1], uz = p[b + 2] - p[a + 2];
        const vx = p[c] - p[a], vy = p[c + 1] - p[a + 1], vz = p[c + 2] - p[a + 2];
        const nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx;
        const len = Math.hypot(nx, ny, nz);
        if (len === 0) continue;
        f[o] = sign * nx / len; f[o + 1] = sign * ny / len; f[o + 2] = sign * nz / len;
        f[o + 3] = f[o] * p[a] + f[o + 1] * p[a + 1] + f[o + 2] * p[a + 2];
        f[o + 4] = len / 2;
    }
    return f;
}

/** The same solid moved by `d`. */
export function moveSolid(solid: FitSolid, d: Vec3): FitSolid {
    const p = new Float64Array(solid.positions.length);
    for (let i = 0; i < p.length; i += 3) { p[i] = solid.positions[i] + d[0]; p[i + 1] = solid.positions[i + 1] + d[1]; p[i + 2] = solid.positions[i + 2] + d[2]; }
    const f = Float64Array.from(solid.faces);
    for (let o = 0; o < f.length; o += FACE) f[o + 3] += f[o] * d[0] + f[o + 1] * d[1] + f[o + 2] * d[2];
    return { shape: solid.shape, positions: p, faces: f, lo: solid.lo.map((v, k) => v + d[k]) as Vec3, hi: solid.hi.map((v, k) => v + d[k]) as Vec3 };
}

const RAYS: Vec3[] = [[1, 0.371, 0.529], [0.231, 1, 0.417], [0.313, 0.619, 1]];

/**
 * Whether a point is strictly inside a closed solid; a point on its surface is not. Convex solids test the
 * faces' half-spaces; others count signed ray crossings in three skewed directions and take two that agree,
 * so overlapping shells merged into one piece don't cancel each other out.
 */
export function solidContains(s: FitSolid, x: number, y: number, z: number): boolean {
    if (!s.shape.closed) return false;
    const size = Math.max(s.hi[0] - s.lo[0], s.hi[1] - s.lo[1], s.hi[2] - s.lo[2]);
    const eps = size * 1e-7;
    if (x <= s.lo[0] + eps || x >= s.hi[0] - eps || y <= s.lo[1] + eps || y >= s.hi[1] - eps || z <= s.lo[2] + eps || z >= s.hi[2] - eps) return false;
    const f = s.faces;
    if (s.shape.convex) {
        for (let o = 0; o < f.length; o += FACE) if (f[o + 4] > 0 && f[o] * x + f[o + 1] * y + f[o + 2] * z - f[o + 3] > -eps) return false;
        return true;
    }
    const tri = s.shape.triangles, grids = rayGrids(s);
    let agreed = 0, disagreed = 0;
    for (let r = 0; r < RAYS.length; r++) {
        const [dx, dy, dz] = RAYS[r], g = grids[r];
        let winding = 0, ambiguous = false;
        if (g) {
            const cell = cellAt(g, x, y, z);
            if (cell >= 0) {
                for (let k = g.start[cell]; k < g.start[cell + 1]; k++) {
                    const w = crossing(s, g.list[k] * 3, x, y, z, dx, dy, dz, eps);
                    if (w === AMBIGUOUS) { ambiguous = true; break; }
                    winding += w;
                }
            }
        } else {
            for (let t = 0; t < tri.length; t += 3) {
                const w = crossing(s, t, x, y, z, dx, dy, dz, eps);
                if (w === AMBIGUOUS) { ambiguous = true; break; }
                winding += w;
            }
        }
        if (ambiguous) continue;
        if (winding !== 0) agreed++; else disagreed++;
        if (agreed >= 2) return true;
        if (disagreed >= 2) return false;
    }
    return false;
}

const AMBIGUOUS = 2;

/** What triangle `t` (an index into `triangles`) does to the ray from (x, y, z) along d: 0 misses, ±1 crosses, AMBIGUOUS grazes. */
function crossing(s: FitSolid, t: number, x: number, y: number, z: number, dx: number, dy: number, dz: number, eps: number): number {
    const p = s.positions, tri = s.shape.triangles;
    const a = tri[t] * 3, b = tri[t + 1] * 3, c = tri[t + 2] * 3;
    const e1x = p[b] - p[a], e1y = p[b + 1] - p[a + 1], e1z = p[b + 2] - p[a + 2];
    const e2x = p[c] - p[a], e2y = p[c + 1] - p[a + 1], e2z = p[c + 2] - p[a + 2];
    const hx = dy * e2z - dz * e2y, hy = dz * e2x - dx * e2z, hz = dx * e2y - dy * e2x;
    const det = e1x * hx + e1y * hy + e1z * hz;
    if (Math.abs(det) < 1e-18) return 0;
    const sx = x - p[a], sy = y - p[a + 1], sz = z - p[a + 2];
    const u = (sx * hx + sy * hy + sz * hz) / det;
    if (u < -1e-9 || u > 1 + 1e-9) return 0;
    const qx = sy * e1z - sz * e1y, qy = sz * e1x - sx * e1z, qz = sx * e1y - sy * e1x;
    const v = (dx * qx + dy * qy + dz * qz) / det;
    if (v < -1e-9 || u + v > 1 + 1e-9) return 0;
    const dist = (e2x * qx + e2y * qy + e2z * qz) / det;
    if (dist < -eps) return 0;
    if (dist <= eps || Math.min(u, v, 1 - u - v) < 1e-9) return AMBIGUOUS;
    return det > 0 ? 1 : -1;
}

/**
 * Per ray direction, a grid over the plane across the ray: each cell lists the triangles whose projected bounds
 * reach it, so a point tests only the triangles its ray can cross instead of all of them. The bounds are padded far
 * beyond `crossing`'s 1e-9 barycentric tolerance, so the answers are exactly the all-triangles answers.
 */
interface RayGrid {
    readonly u: Vec3; readonly v: Vec3;
    readonly u0: number; readonly v0: number; readonly inv: number; readonly n: number;
    readonly start: Uint32Array; readonly list: Uint32Array;
}

const GRID_MIN_TRIANGLES = 96;
const GRID_CACHE = new WeakMap<FitSolid, (RayGrid | null)[]>();

function rayGrids(s: FitSolid): (RayGrid | null)[] {
    let g = GRID_CACHE.get(s);
    if (!g) { g = RAYS.map(d => (s.shape.triangles.length >= GRID_MIN_TRIANGLES * 3 ? buildGrid(s, d) : null)); GRID_CACHE.set(s, g); }
    return g;
}

function cellAt(g: RayGrid, x: number, y: number, z: number): number {
    const i = Math.floor((x * g.u[0] + y * g.u[1] + z * g.u[2] - g.u0) * g.inv), j = Math.floor((x * g.v[0] + y * g.v[1] + z * g.v[2] - g.v0) * g.inv);
    return i < 0 || j < 0 || i >= g.n || j >= g.n ? -1 : j * g.n + i;
}

function buildGrid(s: FitSolid, d: Vec3): RayGrid {
    const len = Math.hypot(d[0], d[1], d[2]), n3: Vec3 = [d[0] / len, d[1] / len, d[2] / len];
    const axis = Math.abs(n3[0]) <= Math.abs(n3[1]) && Math.abs(n3[0]) <= Math.abs(n3[2]) ? 0 : Math.abs(n3[1]) <= Math.abs(n3[2]) ? 1 : 2;
    const a: Vec3 = [0, 0, 0]; a[axis] = 1;
    const cross = (p: Vec3, q: Vec3): Vec3 => [p[1] * q[2] - p[2] * q[1], p[2] * q[0] - p[0] * q[2], p[0] * q[1] - p[1] * q[0]];
    let u = cross(n3, a); const ul = Math.hypot(u[0], u[1], u[2]); u = [u[0] / ul, u[1] / ul, u[2] / ul];
    const v = cross(n3, u);
    const pos = s.positions, tri = s.shape.triangles, count = tri.length / 3;
    const size = Math.max(s.hi[0] - s.lo[0], s.hi[1] - s.lo[1], s.hi[2] - s.lo[2]), pad = size * 1e-6;
    // projected bounds per triangle
    const box = new Float64Array(count * 4);
    let u0 = Infinity, v0 = Infinity, u1 = -Infinity, v1 = -Infinity;
    for (let t = 0; t < count; t++) {
        let lu = Infinity, lv = Infinity, hu = -Infinity, hv = -Infinity;
        for (let k = 0; k < 3; k++) {
            const o = tri[t * 3 + k] * 3, pu = pos[o] * u[0] + pos[o + 1] * u[1] + pos[o + 2] * u[2], pv = pos[o] * v[0] + pos[o + 1] * v[1] + pos[o + 2] * v[2];
            if (pu < lu) lu = pu; if (pu > hu) hu = pu; if (pv < lv) lv = pv; if (pv > hv) hv = pv;
        }
        box[t * 4] = lu - pad; box[t * 4 + 1] = lv - pad; box[t * 4 + 2] = hu + pad; box[t * 4 + 3] = hv + pad;
        if (lu - pad < u0) u0 = lu - pad; if (lv - pad < v0) v0 = lv - pad; if (hu + pad > u1) u1 = hu + pad; if (hv + pad > v1) v1 = hv + pad;
    }
    const n = Math.max(4, Math.min(128, Math.round(Math.sqrt(count / 2))));
    const inv = n / Math.max(u1 - u0, v1 - v0, 1e-300);
    const range = (lo: number, hi: number, o: number): [number, number] => [Math.max(0, Math.floor((lo - o) * inv)), Math.min(n - 1, Math.floor((hi - o) * inv))];
    const start = new Uint32Array(n * n + 1);
    const cells = (t: number, visit: (cell: number) => void): void => {
        const [i0, i1] = range(box[t * 4], box[t * 4 + 2], u0), [j0, j1] = range(box[t * 4 + 1], box[t * 4 + 3], v0);
        for (let j = j0; j <= j1; j++) for (let i = i0; i <= i1; i++) visit(j * n + i);
    };
    for (let t = 0; t < count; t++) cells(t, c => { start[c + 1]++; });
    for (let c = 0; c < n * n; c++) start[c + 1] += start[c];
    const list = new Uint32Array(start[n * n]), fill = start.slice(0, n * n);
    for (let t = 0; t < count; t++) cells(t, c => { list[fill[c]++] = t; });
    return { u, v, u0, v0, inv, n, start, list };
}

/** Numbers per face in `FitSolid.faces`. */
export const FACE_STRIDE = FACE;
